import type { ClawUploadAcceptKind, TextPromptWithInputItem } from '@/app/types';
import type { ClawDraftFileType, QuickPrompt, UploadClawFileResult } from '../../../../types';
import { UPLOAD_ACCEPT_KINDS } from './claw-template-token-attrs';

// 内联自 File/utils.ts，避免拖入 clsx/tailwind 等重依赖
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const VIDEO_EXTS = ['mp4', 'webm', 'mov', 'm4v', 'avi'];
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'aac', 'm4a'];

function getFileExt(p: string): string {
  const v = p.split(/[?#]/)[0].toLowerCase();
  const i = v.lastIndexOf('.');
  return i === -1 ? '' : v.slice(i + 1);
}

function getPreviewTypeFromPath(p: string): 'image' | 'video' | 'audio' | 'other' {
  const ext = getFileExt(p);
  if (IMAGE_EXTS.includes(ext)) return 'image';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  return 'other';
}

// 文件名 token 字符集：仅 ASCII 字母数字与 . _ -，遇到中文/空格自然结束
const FILE_NAME_TOKEN_RE = /^[A-Za-z0-9._-]+/;
const TEMPLATE_TOKEN_RE = /^\[([^\]]+)\]/;

export const QUICK_CASES_FOLDER = 'media/quick_cases';
export const MENTION_COLOR = '#508DFF';

export const extractFileNameFromUrl = (url: string): string => {
  const last = url.split('/').pop() ?? '';
  return last.split('?')[0] ?? '';
};

export const inferFileTypeFromPath = (path: string): ClawDraftFileType => {
  const previewType = getPreviewTypeFromPath(path);
  if (previewType === 'image' || previewType === 'video' || previewType === 'audio') {
    return previewType;
  }
  return 'file';
};

const pushTextToken = (result: TextPromptWithInputItem, text: string): void => {
  if (!text) return;
  const last = result[result.length - 1];
  if (last?.type === 'text') {
    last.text += text;
    return;
  }
  result.push({ type: 'text', text });
};

const parseTemplateTokenParts = (rawToken: string): { type: string; name: string; payload: string } | null => {
  const firstSeparatorIndex = rawToken.indexOf(':');
  if (firstSeparatorIndex <= 0) return null;
  const secondSeparatorIndex = rawToken.indexOf(':', firstSeparatorIndex + 1);
  if (secondSeparatorIndex <= firstSeparatorIndex + 1) return null;
  const type = rawToken.slice(0, firstSeparatorIndex).trim();
  const name = rawToken.slice(firstSeparatorIndex + 1, secondSeparatorIndex).trim();
  const payload = rawToken.slice(secondSeparatorIndex + 1).trim();
  if (!type || !name) return null;
  return { type, name, payload };
};

const parseSelectOptions = (payload: string): Array<{ label: string; value: string }> => {
  return payload
    .split(/[,，]/)
    .map((option) => option.trim())
    .filter(Boolean)
    .map((option) => ({ label: option, value: option }));
};

/** upload 槽第三段（payload）解析成接受的文件类型；非法/空值过滤掉，全空则不限制（返回 undefined）。 */
const parseUploadAccept = (payload: string): ClawUploadAcceptKind[] | undefined => {
  const accept = payload
    .split(/[,，]/)
    .map((item) => item.trim().toLowerCase())
    .filter((item): item is ClawUploadAcceptKind => UPLOAD_ACCEPT_KINDS.has(item as ClawUploadAcceptKind));
  const deduped = Array.from(new Set(accept));
  return deduped.length ? deduped : undefined;
};

const makeClawTemplateToken = (
  index: number,
  rawToken: string,
): Extract<TextPromptWithInputItem[number], { type: 'claw-template-token' }> | null => {
  const tokenParts = parseTemplateTokenParts(rawToken);
  if (!tokenParts) return null;
  const { type, name, payload } = tokenParts;

  if (type === 'skill') {
    return {
      type: 'claw-template-token',
      clawTemplateToken: {
        id: `template-skill-${index}`,
        kind: 'skill',
        label: name,
        value: payload || undefined,
        source: 'platform',
      },
    };
  }
  if (type === 'upload') {
    return {
      type: 'claw-template-token',
      clawTemplateToken: {
        id: `template-upload-${index}`,
        kind: 'upload',
        label: name,
        accept: parseUploadAccept(payload),
      },
    };
  }
  if (type === 'select') {
    const options = parseSelectOptions(payload);
    if (!options.length) return null;
    return {
      type: 'claw-template-token',
      clawTemplateToken: {
        id: `template-select-${index}`,
        kind: 'select',
        label: name,
        value: options[0].value,
        options,
      },
    };
  }
  if (type === 'input') {
    return {
      type: 'claw-template-token',
      clawTemplateToken: {
        id: `template-input-${index}`,
        kind: 'input',
        label: name,
        placeholder: payload || name,
      },
    };
  }
  return null;
};

export interface DraftAttachmentBase extends UploadClawFileResult {
  removable: 'remote' | 'local';
  rawFile?: File;
  source?: 'quick';
}

export const isPendingUploadAttachment = (file: DraftAttachmentBase): file is DraftAttachmentBase & { rawFile: File } =>
  !!file.rawFile;

/**
 * entitled（未开通）态点 quick case 时，把 case 的远端资源以"展示型附件"形式直接放进 attachments：
 * file_path 用 `quick-preview://` 前缀，开通成功后由 ChatInput 内部 effect 真上传到 media/quick_cases。
 */
export const makeQuickPreviewAttachment = (
  asset: NonNullable<QuickPrompt['assets']>[number],
  seq: number,
): DraftAttachmentBase | null => {
  const url = asset.url;
  if (!url) return null;
  const fileName = extractFileNameFromUrl(url) || `quick-case-${seq}`;
  const fileType: ClawDraftFileType =
    asset.type === 'image' || asset.type === 'video' || asset.type === 'audio' ? asset.type : 'file';
  const isVisual = fileType === 'image' || fileType === 'video';
  return {
    file_path: `quick-preview://${seq}/${fileName}`,
    file_name: fileName,
    file_type: fileType,
    preview_url: isVisual ? url : undefined,
    removable: 'local',
    source: 'quick',
  };
};

/**
 * 把 quick prompt 字符串解析为编辑器节点：
 * - `@<filename>` 且 filename 命中 assets 时转为 mention；
 * - `[type:name:payload]` 转为模板槽位，例如 `[skill:口播种草:/skill.png]`。
 */
export const parseQuickPromptToInputValue = (
  item: QuickPrompt,
  getMentionId: (fileName: string) => string = (fileName) => `${QUICK_CASES_FOLDER}/${fileName}`,
): TextPromptWithInputItem => {
  const assetMap = new Map<string, NonNullable<QuickPrompt['assets']>[number]>();
  for (const asset of item.assets ?? []) {
    if (!asset.url) continue;
    const name = extractFileNameFromUrl(asset.url);
    if (name) assetMap.set(name, asset);
  }

  const result: TextPromptWithInputItem = [];
  const { prompt } = item ?? {};
  let textBuffer = '';
  const sourcePrompt = prompt ?? '';
  let i = 0;

  while (i < sourcePrompt.length) {
    if (sourcePrompt[i] === '@') {
      const tokenMatch = sourcePrompt.slice(i + 1).match(FILE_NAME_TOKEN_RE);
      const token = tokenMatch?.[0] ?? '';
      if (token && assetMap.has(token)) {
        if (textBuffer) {
          pushTextToken(result, textBuffer);
          textBuffer = '';
        }
        result.push({
          type: 'mention',
          mention: {
            id: getMentionId(token),
            name: token,
            color: MENTION_COLOR,
          },
        });
        i += 1 + token.length;
        continue;
      }
    }
    if (sourcePrompt[i] === '[') {
      const templateMatch = sourcePrompt.slice(i).match(TEMPLATE_TOKEN_RE);
      const tokenLabel = templateMatch?.[1] ?? '';
      const templateToken = tokenLabel ? makeClawTemplateToken(i, tokenLabel) : null;
      if (templateToken) {
        if (textBuffer) {
          pushTextToken(result, textBuffer);
          textBuffer = '';
        }
        result.push(templateToken);
        i += templateMatch?.[0].length ?? 0;
        continue;
      }
    }
    textBuffer += sourcePrompt[i] ?? '';
    i += 1;
  }

  if (textBuffer) {
    pushTextToken(result, textBuffer);
  }

  return result.length > 0 ? result : [{ type: 'text', text: sourcePrompt }];
};
